"use client";

import { useId, useState } from "react";

export type FaqItem = { q: string; a: string };

export function FaqAccordion({
  faqs,
  className = "",
  onToggle,
}: {
  faqs: FaqItem[];
  className?: string;
  onToggle?: (question: string, open: boolean) => void;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = useId();

  return (
    <div className={`overflow-hidden rounded-[10px] border border-[var(--border-subtle)] ${className}`}>
      {faqs.map((faq, i) => {
        const open = openIndex === i;
        const panelId = `${baseId}-panel-${i}`;
        const buttonId = `${baseId}-button-${i}`;
        return (
          <div key={faq.q} className="border-b border-[var(--border-soft)] last:border-b-0">
            <h3 className="m-0">
              <button
                type="button"
                id={buttonId}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => {
                  const next = open ? null : i;
                  setOpenIndex(next);
                  onToggle?.(faq.q, next !== null);
                }}
                className="flex w-full cursor-pointer items-center justify-between gap-4 border-0 px-5 py-[18px] text-left font-[inherit] text-[var(--foreground)] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--accent)]"
                style={{
                  background: open ? "color-mix(in srgb, var(--accent) 6%, transparent)" : "transparent",
                }}
              >
                <span className="text-[15px] font-semibold">{faq.q}</span>
                <span
                  className="shrink-0 text-xl text-[var(--accent)] transition-transform duration-200"
                  aria-hidden
                  style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
                >
                  +
                </span>
              </button>
            </h3>
            <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!open}>
              <p className="m-0 px-5 pb-5 text-sm leading-[1.75] text-[var(--muted)]">{faq.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
